'use client';

import { useState, useEffect, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { supabase, isSupabaseAvailable } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Eye, EyeOff, Truck, Mail, AlertCircle, Loader2, UserPlus, Shield } from 'lucide-react';

function LoginPageContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // Check URL parameters for redirect and action
  const redirectPath = searchParams?.get('redirect');
  const action = searchParams?.get('action');
  const message = searchParams?.get('message');
  
  useEffect(() => {
    // If this is a trial signup or signup action, default to sign-up mode
    if (action === 'trial' || action === 'signup') {
      setIsSignUp(true);
    }

    // Show success message for password updates
    if (message === 'password_updated') {
      setSuccessMessage('Password updated successfully! You can now sign in with your new password.');
    }
  }, [action, message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('Form submission started', { isSignUp, email });

    if (!isSupabaseAvailable() || !supabase) {
      setError('Authentication service is not available. Please try again later.');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (isSignUp) {
        console.log('Starting sign up process for:', email);
        // Sign up new user
        const { data, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback`,
          },
        });

        console.log('Sign up response:', { data, error: authError });

        if (authError) throw authError;

        if (data.user) {
          console.log('User created successfully:', { userId: data.user.id, needsConfirmation: !data.user.email_confirmed_at });

          // Check if user needs email confirmation
          if (!data.user.email_confirmed_at) {
            // Show success message for email confirmation
            setSuccessMessage('Account created! Please check your email and click the confirmation link to complete your account setup.');
            setLoading(false);
            return;
          }

          // Profile creation is handled automatically by database trigger
          // Redirect based on URL parameters or default to onboarding
          const destination = redirectPath === 'onboarding' ? '/onboarding' : '/onboarding';
          console.log('Redirecting to:', destination);
          router.push(destination);
        } else {
          console.log('No user returned from signup');
          setError('Account creation failed. Please try again.');
        }
      } else {
        // Sign in existing user
        const { data, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (authError) throw authError;

        if (data.user) {
          // Check if user has admin role
          const { data: roleData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', data.user.id)
            .maybeSingle();

          // Check if user profile exists
          const { data: profileData } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('user_id', data.user.id)
            .single();

          // If admin role exists, go to admin dashboard
          if (roleData?.role) {
            router.push('/dashboard');
          } else {
            // Check if this is a trial flow
            if (redirectPath === 'onboarding' && action === 'trial') {
              router.push('/onboarding');
            } else {
              // Regular user - go to user dashboard
              router.push('/user-dashboard');
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      let errorMessage = 'Authentication failed. Please try again.';

      if (isSignUp) {
        // Sign-up specific error messages
        if (error.message?.includes('already registered')) {
          errorMessage = 'This email is already registered. Try signing in instead.';
        } else if (error.message?.includes('Password')) {
          errorMessage = 'Password must be at least 6 characters long.';
        } else {
          errorMessage = error.message || 'Account creation failed. Please try again.';
        }
      } else {
        // Sign-in specific error messages
        if (error.message?.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials or create an account.';
        } else {
          errorMessage = error.message || 'Sign in failed. Please try again.';
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address to reset your password.');
      return;
    }

    if (!isSupabaseAvailable() || !supabase) {
      setError('Authentication service is not available. Please try again later.');
      return;
    }

    setResetLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/auth/callback?reset=true`,
      });

      if (error) throw error;

      setSuccessMessage('Password reset email sent! Check your inbox and follow the instructions to reset your password.');
      setShowForgotPassword(false);
    } catch (error: any) {
      console.error('Password reset error:', error);
      let errorMessage = 'Failed to send password reset email. Please try again.';

      if (error.message?.includes('not found')) {
        errorMessage = 'No account found with this email address. Please check your email or create a new account.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-blue-50 to-indigo-100 flex items-center justify-center relative overflow-hidden">
      {/* Background truck elements */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 border-8 border-blue-300 rounded-full transform rotate-45"></div>
        <div className="absolute top-40 right-20 w-24 h-24 border-6 border-indigo-300 rounded-full transform -rotate-12"></div>
        <div className="absolute bottom-32 left-1/4 w-28 h-28 border-7 border-sky-300 rounded-full transform rotate-30"></div>
      </div>

      <div className="w-full max-w-md px-6 relative z-10">
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-md relative overflow-hidden">
          {/* Truck background */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-4 right-4 w-12 h-12 border-2 border-blue-300 rounded-full"></div>
            <div className="absolute bottom-8 left-6 w-8 h-8 border-2 border-indigo-300 rounded-full"></div>
          </div>

          <CardHeader className="text-center pb-8 relative z-10">
            <div className="w-20 h-20 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Truck className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="font-bungee text-3xl text-slate-800 mb-2">
              {isSignUp
                ? 'Join the Adventure!'
                : 'Welcome Back!'
              }
            </CardTitle>
            <p className="text-slate-600 text-lg">
              {isSignUp
                ? 'Create your Monster Truck Stories account'
                : 'Sign in to your Monster Truck Stories world'
              }
            </p>
            {/* Debug info - remove in production */}
            {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-400 mt-2">
                Mode: {isSignUp ? 'Sign Up' : 'Sign In'} | Action: {action || 'none'}
              </div>
            )}
            {false ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-4">
                <p className="text-green-700 text-sm font-semibold">
                  🎉 Subscription Required
                </p>
              </div>
            ) : (
              <p className="text-slate-500 text-sm mt-2">
                Where stories come to life! 🚛✨
              </p>
            )}
          </CardHeader>

          <CardContent className="relative z-10 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {successMessage && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <Mail className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <p className="text-green-700 text-sm">{successMessage}</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700 font-semibold">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="parent@example.com"
                  required
                  disabled={loading}
                  className="h-12 bg-white/80 border-slate-200 focus:border-blue-400 focus:ring-blue-400"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 font-semibold">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={isSignUp ? "Create a secure password" : "Enter your password"}
                    required
                    disabled={loading}
                    className="h-12 bg-white/80 border-slate-200 focus:border-blue-400 focus:ring-blue-400 pr-12"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0 hover:bg-slate-100"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-slate-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-slate-500" />
                    )}
                  </Button>
                </div>
                {!isSignUp && (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowForgotPassword(true)}
                      disabled={loading}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-sm p-0 h-auto"
                    >
                      Forgot password?
                    </Button>
                  </div>
                )}
              </div>

              {/* Forgot Password Modal */}
              {showForgotPassword && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                  <Card className="bg-white w-full max-w-md">
                    <CardHeader className="text-center">
                      <CardTitle className="text-xl text-slate-800">Reset Password</CardTitle>
                      <p className="text-slate-600 text-sm">
                        Enter your email address and we'll send you a link to reset your password.
                      </p>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="reset-email" className="text-slate-700">
                            Email Address
                          </Label>
                          <Input
                            id="reset-email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                            disabled={resetLoading}
                            className="h-12"
                          />
                        </div>

                        <div className="flex gap-3">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowForgotPassword(false)}
                            disabled={resetLoading}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={resetLoading || !email}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                          >
                            {resetLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              'Send Reset Link'
                            )}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full h-12 bg-gradient-to-r from-orange-400 to-red-500 hover:from-orange-500 hover:to-red-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {isSignUp ? 'Creating Account...' : 'Signing In...'}
                  </>
                ) : (
                  <>
                    {isSignUp ? (
                      <>
                        <UserPlus className="w-5 h-5 mr-2" />
                        Create My Account
                      </>
                    ) : (
                      <>
                        <Truck className="w-5 h-5 mr-2" />
                        Start My Adventure
                      </>
                    )}
                  </>
                )}
              </Button>
            </form>

            {/* Toggle Sign Up / Sign In */}
            <div className="flex items-center gap-4 my-8">
              <hr className="flex-1 border-slate-200" />
              <span className="text-slate-500 text-sm">or</span>
              <hr className="flex-1 border-slate-200" />
            </div>

            <div className="text-center">
              <p className="text-slate-600 text-sm mb-4">
                {isSignUp ? 'Already have an account?' : "Don't have an account yet?"}
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError(null);
                  setSuccessMessage(null);
                }}
                disabled={loading}
                className="w-full bg-white/80 hover:bg-white text-slate-700 border-slate-300"
              >
                {isSignUp ? 'Sign In Instead' : 'Create New Account'}
              </Button>
            </div>

            {/* Admin login link */}
            <div className="text-center mt-6 pt-6 border-t border-slate-200">
              <p className="text-slate-500 text-xs mb-2">Staff Member?</p>
              <Button
                variant="ghost"
                onClick={() => router.push('/admin')}
                disabled={loading}
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 text-sm"
              >
                <Shield className="w-4 h-4 mr-1" />
                Admin Access
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8">
          <Button
            variant="ghost"
            onClick={() => router.push('/')}
            disabled={loading}
            className="text-slate-500 hover:text-slate-700"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-br from-sky-100 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    }>
      <LoginPageContent />
    </Suspense>
  );
}